import React, { useEffect, useState } from 'react'

import { Button, Grid, Paper } from '@mui/material';

import { AnnotationButtonGroup, AnnotationIconButton } from '~/components/AnnotationButton';
import {
  ColorIcon,
  CrosshairIcon,
  CursorIcon,
  DeleteIcon,
  EditIcon,
  RectangleIcon,
  Stroke1Icon,
  Stroke2Icon,
  Stroke3Icon,
  TextIcon,
} from '~/components/icons';
import { Tooltip } from '~/components/Tooltip';
import { ANNOTATION_COLORS, ANNOTATION_STROKES, useCanvas } from '~/contexts/CanvasContext';
import { useSidePanel } from '~/contexts/SidePanelContext';
import { EscapeKey } from '~/keyboard';

interface IAnnotationTools {
  canvasRef: React.MutableRefObject<fabric.Canvas>;
  disabled: boolean;
}

const STROKE_ICONS = {
  'small': Stroke1Icon,
  'medium': Stroke2Icon,
  'large': Stroke3Icon,
};

const TOOLTIP_SELECTION = { position: 'absolute', top: -46, left: -23 };

export const AnnotationToolbar: React.FC<IAnnotationTools> = (props) => {
  const { canvasRef, disabled } = props;
  const {
    annotationEnabled,
    mode: currentMode,
    color: currentColor,
    stroke: currentStroke,
    update,
    getBlob,
    clear,
  } = useCanvas();
  const { attach } = useSidePanel();
  const CurrentStrokeIcon = STROKE_ICONS[currentStroke];
  const colors = Object.keys(ANNOTATION_COLORS);
  const strokes = Object.keys(ANNOTATION_STROKES);
  const [tooltip, openTooltip] = useState<'color' | 'stroke' | 'none'>('none');

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === EscapeKey) {
        update({ mode: 'none', annotationEnabled: false });
      }
    }
    if (annotationEnabled) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      }
    }
  }, [annotationEnabled, update]);

  if (!annotationEnabled) {
    return null;
  }

  return (
    <Paper
      sx={{
        display: 'flex',
        padding: 2,
        alignItems: 'center',
        zIndex: 3,
        width: 800,
        maxWidth: '100%',
        position: 'absolute',
        mx: 'auto',
        mt: 1.5,
        left: 0,
        right: 0,
      }}
    >
      <AnnotationButtonGroup sx={{ mr: '18px' }}>
        <Tooltip title="Move">
          <div>
            <AnnotationIconButton
              selected={currentMode === 'none'}
              disabled={disabled}
              onClick={() => update({ mode: 'none' })}
            >
              <CursorIcon sx={{ fontSize: 16 }} />
            </AnnotationIconButton>
          </div>
        </Tooltip>
        <Tooltip title="Rectangle">
          <div>
            <AnnotationIconButton
              selected={currentMode === 'rect'}
              disabled={disabled}
              onClick={() => update({ mode: 'rect' })}
            >
              <RectangleIcon sx={{ fontSize: 24 }} />
            </AnnotationIconButton>
          </div>
        </Tooltip>
        <Tooltip title="Free Draw">
          <div>
            <AnnotationIconButton
              selected={currentMode === 'draw'}
              disabled={disabled}
              onClick={() => update({ mode: 'draw' })}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </AnnotationIconButton>
          </div>
        </Tooltip>
        <Tooltip title="Text">
          <div>
            <AnnotationIconButton
              selector="stroke"
              disabled={disabled}
              selected={currentMode === 'text'}
              onClick={() => update({ mode: 'text' })}
            >
              <TextIcon sx={{ fontSize: 18 }} />
            </AnnotationIconButton>
          </div>
        </Tooltip>
      </AnnotationButtonGroup>
      <Tooltip
        disableFocusListener
        disableHoverListener
        disableTouchListener
        noSpacing
        open={tooltip === 'color'}
        title={
          <AnnotationButtonGroup sx={TOOLTIP_SELECTION}>
            {colors.map((color: keyof typeof ANNOTATION_COLORS) => {
              return (
                <AnnotationIconButton
                  key={color}
                  disabled={disabled}
                  onClick={() => {
                    update({ color });
                    openTooltip('none');
                  }}
                >
                  <ColorIcon sx={{ fontSize: 16, circle: { fill: ANNOTATION_COLORS[color] } }} />
                </AnnotationIconButton>
              );
            })}
            <AnnotationIconButton
              disabled={disabled}
              onClick={() => openTooltip('none')}
            >
              <CrosshairIcon
                sx={{ fontSize: 16, transform: 'rotateY(0deg) rotate(45deg)' }} />
            </AnnotationIconButton>
          </AnnotationButtonGroup>
        }
      >
        <AnnotationButtonGroup sx={{ mr: '4px' }}>
          <AnnotationIconButton
            disabled={disabled}
            onClick={() => openTooltip('color')}
          >
            <ColorIcon
              sx={{ fontSize: 16, circle: { fill: ANNOTATION_COLORS[currentColor] } }}
            />
          </AnnotationIconButton>
        </AnnotationButtonGroup>
      </Tooltip>
      <Tooltip
        disableFocusListener
        disableHoverListener
        disableTouchListener
        noSpacing
        open={tooltip === 'stroke'}
        title={
          <AnnotationButtonGroup sx={TOOLTIP_SELECTION}>
            {strokes.map((stroke: keyof typeof ANNOTATION_STROKES) => {
              const Icon = STROKE_ICONS[stroke];
              return (
                <AnnotationIconButton
                  key={stroke}
                  disabled={disabled}
                  onClick={() => {
                    update({ stroke });
                    openTooltip('none');
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                </AnnotationIconButton>
              );
            })}
            <AnnotationIconButton
              disabled={disabled}
              onClick={() => openTooltip('none')}
            >
              <CrosshairIcon
                sx={{ fontSize: 16, transform: 'rotateY(0deg) rotate(45deg)' }} />
            </AnnotationIconButton>
          </AnnotationButtonGroup>
        }
      >
        <AnnotationButtonGroup sx={{ mr: '18px' }}>
          <AnnotationIconButton
            disabled={disabled}
            onClick={() => openTooltip('stroke')}
          >
            <CurrentStrokeIcon sx={{ fontSize: 16 }} />
          </AnnotationIconButton>
        </AnnotationButtonGroup>
      </Tooltip>
      <AnnotationButtonGroup sx={{ mr: '4px' }}>
        <Tooltip title="Delete">
          <div>
            <AnnotationIconButton
              selected={currentMode === 'eraser'}
              disabled={disabled}
              onClick={() => update({ mode: 'eraser' })}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </AnnotationIconButton>
          </div>
        </Tooltip>
      </AnnotationButtonGroup>
      <Grid container columnSpacing={1} justifyContent="flex-end" width="100%">
        <Grid item>
          <Button
            color="secondary"
            size="small"
            onClick={() => {
              clear(canvasRef);
              update({ mode: 'none', annotationEnabled: false });
              openTooltip('none');
            }}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            size="small"
            disabled={disabled}
            onClick={async () => {
              const blob = await getBlob(canvasRef);
              if (blob) {
                attach(blob);
              }
              update({ mode: 'none', annotationEnabled: false });
              openTooltip('none');
            }}
          >
            Attach
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}
